/**
 * Clinic Financial Dashboard — Stripe Connect embedded components.
 *
 * Renders Payments, Payouts, and Account Management tabs for connected clinics.
 * All data comes directly from Stripe — zero custom reporting code.
 */
import React, { useState } from 'react';
import {
  ConnectPayments,
  ConnectPayouts,
  ConnectAccountManagement,
} from '@stripe/react-connect-js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Wallet, Settings } from 'lucide-react';
import { StripeConnectProvider } from './StripeConnectProvider';

interface ClinicFinancialDashboardProps {
  accountId: string;
  clinicName?: string;
}

type Tab = 'payments' | 'payouts' | 'account';

const TAB_CONFIG: { id: Tab; label: string; icon: React.ReactNode; desc: string }[] = [
  {
    id: 'payments',
    label: 'Payments',
    icon: <CreditCard className="h-4 w-4" />,
    desc: 'Transaction history, refunds, disputes',
  },
  {
    id: 'payouts',
    label: 'Payouts',
    icon: <Wallet className="h-4 w-4" />,
    desc: 'Balance, payout schedule, instant payouts',
  },
  {
    id: 'account',
    label: 'Account',
    icon: <Settings className="h-4 w-4" />,
    desc: 'Business info, bank account, tax documents',
  },
];

export const ClinicFinancialDashboard: React.FC<ClinicFinancialDashboardProps> = ({
  accountId,
  clinicName,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('payments');

  return (
    <StripeConnectProvider accountId={accountId}>
      <div className="space-y-4">
        {/* Tab Navigation */}
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-100">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all flex-1 justify-center ${
                activeTab === tab.id
                  ? 'bg-white text-brand-teal shadow-sm border border-slate-200'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">
                {TAB_CONFIG.find((t) => t.id === activeTab)?.label}
              </CardTitle>
              {clinicName && (
                <Badge variant="outline" className="text-[10px] font-normal text-slate-500">
                  {clinicName}
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-400">
              {TAB_CONFIG.find((t) => t.id === activeTab)?.desc}
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="min-h-[400px]">
              {activeTab === 'payments' && <ConnectPayments />}
              {activeTab === 'payouts' && <ConnectPayouts />}
              {activeTab === 'account' && <ConnectAccountManagement />}
            </div>
          </CardContent>
        </Card>
      </div>
    </StripeConnectProvider>
  );
};

export default ClinicFinancialDashboard;
